"use client"

import * as React from "react"
import { useGSAP } from "@gsap/react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { cn } from "@/core/utils/utils"

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP)
}

export type SpatialAlignment = "center" | "left" | "right"
export type SpatialMode = "scrub" | "kinetic"

export interface SpatialTextCascadeProps {
  text: string
  className?: string
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div"
  alignment?: SpatialAlignment
  mode?: SpatialMode
  split?: "words" | "chars"
  blurAmount?: number
  perspective?: number
  scrubDuration?: number | boolean
  startTrigger?: string
  endTrigger?: string
  stagger?: number
  bloomTracking?: boolean
  maskFeather?: boolean
  onComplete?: () => void
}

/**
 * Awwwards SOTD/SOTY Spatial Text Cascade.
 *
 * Implements 3D Depth Folding & Submerging Cascades:
 * - Center: "Waterfall Horizon Fold" (emerges compressed & rotated in 3D, blooming outward with kerning expansion)
 * - Left: "Lateral Seam Cascade" (emerges from the left seam with Y-rotation, skew, and lateral smear)
 * - Right: "Right Seam Cascade" (mirrored lateral emergence from the right seam)
 * - Scrub Mode: Bi-directional continuous scroll coupling (unfolds on down-scroll, submerges on up-scroll)
 * - Kinetic Mode: Velocity-damped entry with automatic filter cleanup for 100% retina text sharpness
 */
export function SpatialTextCascade({
  text,
  className,
  tag = "div",
  alignment = "center",
  mode = "scrub",
  split = "words",
  blurAmount = 18,
  perspective = 1200,
  scrubDuration = 0.8,
  startTrigger = "top 85%",
  endTrigger = "top 35%",
  stagger = 0.04,
  bloomTracking = true,
  maskFeather = false,
  onComplete,
}: SpatialTextCascadeProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const tokensRef = React.useRef<(HTMLSpanElement | null)[]>([])

  // Tokenize text into words or characters
  const tokens = React.useMemo(() => {
    if (split === "chars") {
      return text.split("").map((char, i) => ({
        id: `${i}-${char}`,
        value: char === " " ? "\u00A0" : char,
        isSpace: char === " ",
      }))
    }
    return text.split(" ").map((word, i) => ({
      id: `${i}-${word}`,
      value: word,
      isSpace: false,
    }))
  }, [text, split])

  // Clear tokens ref array size on re-render
  tokensRef.current = []

  useGSAP(
    () => {
      const elements = tokensRef.current.filter(Boolean) as HTMLSpanElement[]
      if (!elements.length || !containerRef.current) return

      // Compute initial 3D transform matrices per alignment archetype
      let fromVars: gsap.TweenVars = {}
      let toVars: gsap.TweenVars = {}

      if (alignment === "center") {
        // Waterfall Horizon Fold
        fromVars = {
          opacity: 0,
          y: 48,
          z: -90,
          rotateX: -42,
          scale: 0.82,
          letterSpacing: bloomTracking ? "-0.06em" : "normal",
          filter: `blur(${blurAmount}px)`,
          transformOrigin: "50% 100% -40px",
          force3D: true,
        }
        toVars = {
          opacity: 1,
          y: 0,
          z: 0,
          rotateX: 0,
          scale: 1,
          letterSpacing: "0em",
          filter: "blur(0px)",
          ease: mode === "scrub" ? "none" : "power4.out",
          stagger: {
            each: stagger,
            from: "center", // Center-out ripple cascade
          },
        }
      } else if (alignment === "left") {
        // Left Lateral Seam Cascade
        fromVars = {
          opacity: 0,
          x: -70,
          y: 16,
          z: -60,
          rotateY: 28,
          skewX: -10,
          scaleX: 0.75,
          scaleY: 0.88,
          letterSpacing: bloomTracking ? "-0.05em" : "normal",
          filter: `blur(${blurAmount}px)`,
          transformOrigin: "0% 50%",
          force3D: true,
        }
        toVars = {
          opacity: 1,
          x: 0,
          y: 0,
          z: 0,
          rotateY: 0,
          skewX: 0,
          scaleX: 1,
          scaleY: 1,
          letterSpacing: "0em",
          filter: "blur(0px)",
          ease: mode === "scrub" ? "none" : "power4.out",
          stagger: {
            each: stagger,
            from: "start",
          },
        }
      } else {
        // Right Lateral Seam Cascade
        fromVars = {
          opacity: 0,
          x: 70,
          y: 16,
          z: -60,
          rotateY: -28,
          skewX: 10,
          scaleX: 0.75,
          scaleY: 0.88,
          letterSpacing: bloomTracking ? "-0.05em" : "normal",
          filter: `blur(${blurAmount}px)`,
          transformOrigin: "100% 50%",
          force3D: true,
        }
        toVars = {
          opacity: 1,
          x: 0,
          y: 0,
          z: 0,
          rotateY: 0,
          skewX: 0,
          scaleX: 1,
          scaleY: 1,
          letterSpacing: "0em",
          filter: "blur(0px)",
          ease: mode === "scrub" ? "none" : "power4.out",
          stagger: {
            each: stagger,
            from: "end",
          },
        }
      }

      if (mode === "scrub") {
        // Bi-directional scroll scrubbing (Unfolds down, submerges up)
        gsap.fromTo(elements, fromVars, {
          ...toVars,
          scrollTrigger: {
            trigger: containerRef.current,
            start: startTrigger,
            end: endTrigger,
            scrub: scrubDuration,
            invalidateOnRefresh: true,
          },
        })
      } else {
        // Kinetic momentum entry with viewport trigger
        gsap.fromTo(elements, fromVars, {
          ...toVars,
          duration: 0.85,
          scrollTrigger: {
            trigger: containerRef.current,
            start: startTrigger,
            once: true,
            onEnter: () => {
              // Ensure clean subpixel raster after animation
              gsap.delayedCall(0.9, () => {
                elements.forEach((el) => {
                  if (el) {
                    el.style.filter = "none"
                    el.style.willChange = "auto"
                  }
                })
                onComplete?.()
              })
            },
          },
        })
      }
    },
    {
      dependencies: [
        text,
        alignment,
        mode,
        split,
        blurAmount,
        scrubDuration,
        startTrigger,
        endTrigger,
        stagger,
        bloomTracking,
      ],
      scope: containerRef,
    }
  )

  const Component = tag || "div"

  const justifyClass =
    alignment === "center"
      ? "justify-center text-center"
      : alignment === "right"
        ? "justify-end text-right"
        : "justify-start text-left"

  return (
    <Component
      ref={containerRef}
      aria-label={text}
      className={cn(
        "inline-flex flex-wrap gap-x-[0.25em] gap-y-[0.1em]",
        justifyClass,
        maskFeather &&
          "[mask-image:linear-gradient(to_bottom,transparent_0%,black_20%,black_80%,transparent_100%)]",
        className
      )}
      style={{
        perspective: `${perspective}px`,
        transformStyle: "preserve-3d",
      }}
    >
      {tokens.map((token, index) => (
        <span
          key={token.id}
          ref={(el) => {
            if (el) tokensRef.current[index] = el
          }}
          className="inline-block overflow-visible will-change-[transform,opacity,filter]"
          aria-hidden="true"
          style={{
            transformStyle: "preserve-3d",
            display: "inline-block",
          }}
        >
          {token.value}
        </span>
      ))}
    </Component>
  )
}
